import { SupplierGetResponseType } from "@sellar-io/api-v2/types";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useIntercom } from "react-use-intercom";
import { useMediaQuery } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";
import { Icon, SpinnerLegacy } from "../../../components";
import { SellarStepper } from "~/components/stepper/stepper";
import { useBasketContext } from "~/hooks";
import { useFirebaseUser } from "../../../hooks/useFirebaseUser.shared";
import {
  SignupFlowStep,
  useSignupContext,
} from "../../../hooks/useSignupContext.shared";
import { StorefrontSignupDialogBasketInfoMobile } from "../../stockList/categoryPage/Storefront/StorefrontDialogs/StorefrontSignupDialog/StorefrontSignupDialogBasketInfoMobile";
import {
  BuyerCreationFlowStep,
  useBuyerCreationFlowStore,
} from "../signup/steps/BuyerCreationStep/BuyerCreationFlowStore";
import { BuyerCreationStep } from "../signup/steps/BuyerCreationStep/BuyerCreationStep";
import { UserOptInMarketingStep } from "../signup/steps/UserOptInMarketingStep/UserOptInMarketingStep";
import { UserSignInStep } from "../signup/steps/UserSignInStep";
import { UserSignupBusinessSelectStep } from "../signup/steps/UserSignupBusinessSelectStep";
import { UserSignupConfirmationStep } from "../signup/steps/UserSignupConfirmationStep";
import { UserSignupDetailsStep } from "../signup/steps/UserSignupDetailsStep";
import { UserSignupEmailStep } from "../signup/steps/UserSignupEmailStep";
import { UserSignupForgotPassword } from "../signup/steps/UserSignupForgotPassword";
import { UserSignupVerificationCodeStep } from "../signup/steps/UserSignupVerificationCodeStep";
import { UserAboutStep } from "../signup/steps/UserAboutStep";
import { CreditEligibleStep } from "../signup/steps/CreditEligibleStep/CreditEligibleStep";
import { getJwtUrl } from "../signup/signupUrl";
import { useSellarSession } from "../../../hooks/useSellarSession";
import { UserSignupFlowContent } from "./UserSignupFlowContent";

const getSignupNumberSteps = (
  currentStep: string,
  hasPriceTier: boolean,
  signupDialogActive: boolean,
  isJoiningBusiness: boolean,
) => {
  if (currentStep === SignupFlowStep.SignInStep.name) {
    return 2;
  }

  if (isJoiningBusiness) {
    return 4;
  }

  if (hasPriceTier || !signupDialogActive) {
    return 5;
  }

  return 6;
};

// this is reused both for direct sign up and for storefront sign up
export const UserSignupFlow = ({
  supplier,
  hasPriceTier = false,
}: {
  supplier?: SupplierGetResponseType;
  hasPriceTier?: boolean;
}) => {
  const intercom = useIntercom();
  const { state: sessionState } = useSellarSession();
  const recoveringSession =
    sessionState.status === "initialization-error" ||
    sessionState.status === "retrying-initialization";
  const [email, setEmail] = useState<string>();
  const { user } = useFirebaseUser();
  const [searchParams] = useSearchParams();
  const jwtUrl = getJwtUrl(searchParams.get("continue"));
  const { signupDialogActive, setSignupDialogActive, step, setStep } =
    useSignupContext();
  const {
    setStep: setBuyerStep,
    step: buyerStep,
    reset,
  } = useBuyerCreationFlowStore(
    useShallow((state) => ({
      setStep: state.setStep,
      step: state.step,
      reset: state.reset,
    })),
  );
  const [{ order }] = useBasketContext();
  const isMobile = useMediaQuery("(max-width: 769px)");
  const stepperSteps = getSignupNumberSteps(
    step?.name ?? "",
    hasPriceTier,
    signupDialogActive,
    !!jwtUrl,
  );

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  const renderStep = () => {
    switch (step?.name) {
      // sign in/up page

      case SignupFlowStep.EmailStep.name:
        return (
          <UserSignupEmailStep
            setEmail={setEmail}
            supplier={supplier}
            previousEmail={email}
          />
        );

      // choose about business info

      case SignupFlowStep.AboutStep.name:
        return (
          <UserAboutStep
            setEmail={setEmail}
            supplier={supplier}
            previousEmail={email}
          />
        );

      case SignupFlowStep.SignInStep.name:
        return <UserSignInStep email={email} />;

      // if the user already has associated supplier account, redirect them to sign in

      case SignupFlowStep.BusinessSelectStep.name:
        return (
          <>
            {supplier && (
              <UserSignupBusinessSelectStep supplierId={supplier.id} />
            )}
          </>
        );

      // email verification step using email code

      case SignupFlowStep.MagicLinkSendStep.name:
        return <>{email && <UserSignupVerificationCodeStep email={email} />}</>;

      // create sellar account with username and password

      case SignupFlowStep.SignupDetails.name:
        // Firebase sign-in resolves before Sellar bootstrap. Never mount an
        // editable form until its user is ready, including in storefront dialogs.
        if (
          sessionState.status !== "ready" &&
          sessionState.status !== "needs-business"
        ) {
          return <SpinnerLegacy />;
        }
        return (
          <>
            {(user?.firebaseUser.email || email) && <UserSignupDetailsStep />}
          </>
        );

      case SignupFlowStep.OptInMarketingStep.name:
        return <UserOptInMarketingStep />;

      // create/join a venue flow - includes find company

      case SignupFlowStep.BuyerCreationStep.name:
        return <BuyerCreationStep />;

      // credit eligible step
      case SignupFlowStep.CreditEligibleStep.name:
        return <CreditEligibleStep />;

      // sign up completion page

      case SignupFlowStep.ConfirmationStep.name:
        return <UserSignupConfirmationStep supplier={supplier} />;

      case SignupFlowStep.ForgotPasswordStep.name:
        return <UserSignupForgotPassword email={email} />;
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className={"grid w-full grid-cols-12 items-center"}>
        <div className={"col-span-2"}>
          {!recoveringSession &&
            (!!step?.backStep ||
              (step?.name === "BuyerCreationStep" &&
                !!buyerStep?.backStep)) && (
              <div className={"col-span-2"}>
                <Icon
                  className={"cursor-pointer"}
                  icon={"CHEVRON_LEFT"}
                  onClick={() => {
                    if (step.backStep) {
                      return setStep(SignupFlowStep[step.backStep]);
                    }
                    if (buyerStep?.backStep) {
                      setBuyerStep(BuyerCreationFlowStep[buyerStep.backStep]);
                    }
                  }}
                />
              </div>
            )}
          {!!step?.dismiss && signupDialogActive && (
            <div className={"col-span-2"}>
              <Icon
                className={"cursor-pointer"}
                icon={"CHEVRON_DOWN"}
                onClick={() => setSignupDialogActive(false)}
              />
            </div>
          )}
        </div>
        <div className={"col-span-8"}>
          {!recoveringSession && step?.currentStepIndex && (
            <>
              <SellarStepper
                steps={stepperSteps}
                activeStep={step.currentStepIndex}
              />
            </>
          )}
        </div>
        <div className={"col-span-2 flex items-end justify-end"}>
          <Icon
            className={"cursor-pointer"}
            onClick={() => intercom.show()}
            icon={"HELP_CIRCLE_OUTLINE"}
          />
        </div>
      </div>
      {supplier && !!order?.OrderItems?.length && isMobile && (
        <StorefrontSignupDialogBasketInfoMobile
          supplier={supplier}
          className={"mt-2"}
        />
      )}
      <UserSignupFlowContent>{renderStep()}</UserSignupFlowContent>
    </div>
  );
};
